/**
 * Route definitions for pre-defined modal dialogs.
 */
import * as React from 'react';

import { AlertType, loadIcon, NakedIcon } from 'theme/icons';
import { Bold, Light, Medium } from 'theme/text';
import { FontSize, FontWeight } from 'theme/fonts';

import { Colors } from 'theme/colors';
import { Container } from 'theme/layout';
import { IconContainer } from 'theme/icons/styles';

import { Description } from '../styles';
import {
  ProspectExpiredContent,
  ProspectExpiredDescription,
  ProspectExpiredHeading,
  RoseModalImage,
} from '../styles.web';

const ICON_SIZE = 20;
const HOURGLASS_ICON_SIZE = 50;
const PADDING_HORIZONTAL = 25;
const PADDING_VERTICAL = 35;
const PAYMENT_PROCESS_ICON_SIZE = 60;

const ROSE_IMAGE = '/images/rose.jpg';

export const MODAL_ROUTES = {
  /** Add multiple photos */
  addPhotos: {
    submitText: 'Done',
    title: <Bold fontSize={FontSize.H3}>Add Photos</Bold>,
  },
  /** Biometrics */
  biometricsLogin: {
    cancelText: 'No, thanks',
    submitText: 'Enable',
    circleColor: Colors.white,
  },
  /** Calendar */
  calendar: {},
  /** Call back confirmation */
  callBack: {
    alertType: AlertType.Success,
    description: 'You can expect our call within the next 24 hours.',
    submitText: 'Okay',
    title: 'Awesome!',
  },
  /** Cancel cover  */
  cancelCover: {
    title: 'Please tell us why...',
  },
  /** Cancel cover feedback  */
  cancelCoverFeedback: {
    submitText: 'Go ahead',
    title: 'Mind telling us more?',
  },
  /** Cancel cover confirmation  */
  cancelCoverConfirmation: {
    cancelText: 'Keep me covered',
    description: (
      <Description>
        Please note that the cancellation will be effective right away and your cover for this item
        will be removed from your list of assets and can not be added again for the next 12 months.
      </Description>
    ),
    submitText: 'Cancel cover',
    title: 'Cancel your cover',
  },
  /** Confirmation of a cancelled cover */
  cancelCoverConfirmed: {
    alertType: AlertType.Success,
    submitText: 'Done',
    title: 'Cover Cancelled',
  },
  /** Car hire modal */
  carHire: {
    description:
      'Happy to be on your bike while your car is being repaired after an accident? If not, get car hire for up to 60 days.',
    submitText: 'Done',
    title: 'Car Hire',
  },
  /** Selection of a cause confirmation */
  causeConfirmation: {
    cancelText: 'Cancel',
    description:
      'If claims are lower than expected they will benefit from your choice.\n\nJust a heads up that your choice of cause will be fixed for the next year.',
    submitText: 'Choose',
  },
  /** Thank you for joining a cause */
  causeThanks: {
    alertType: AlertType.Success,
    accentColor: Colors.white,
    iconColor: Colors.green,
    statusIcon: loadIcon('heart'),
    title: 'Thanks for choosing us!',
  },
  /** Change start date error */
  changeBasketStartDate: {
    alertType: AlertType.Error,
    cancelText: 'Cancel',
    description:
      "Sorry, we couldn't change your start date. Please check your connection and try again.",
    submitText: 'Try again',
    title: 'Please try again.',
  },
  /** Change password confirmation */
  changePasswordConfirmation: {
    alertType: AlertType.Success,
    accentColor: Colors.white,
    iconColor: Colors.green,
    statusIcon: loadIcon('na-password-reset', { width: 57.5, height: 64.5 }),
    title: 'Your password has been reset!',
  },
  changePasswordFailure: {
    description:
      'We could not change your password because the passcode you entered was incorrect.',
    submitText: 'Try again',
    title: 'Sorry!',
  },
  /** Chat message edit confirmation */
  chatMessagePrompt: {
    cancelText: 'Cancel',
    description: "If you edit your response, you'll lose the responses that followed it.",
    submitText: 'Okay',
    title: 'Heads up!',
  },
  /** Cover downgrade notification */
  coverDowngrade: {
    accentColor: Colors.blue,
    cancelText: <Light color={Colors.white}>Cancel</Light>,
    submitText: <Bold color={Colors.white}>Go ahead</Bold>,
    title: 'Heads Up!',
  },
  /** Notifies the user of an issue with their account */
  dashboardError: {
    content: (
      <Description>
        We&apos;re having some trouble displaying your covers.{'\n\n'}
        Please drop us a mail at <Medium color={Colors.green}>help@naked.insure</Medium> and
        we&apos;ll get right on it.
      </Description>
    ),
    hideCloseButton: true,
    onClose: () => true,
    submitText: 'Log me out',
    title: 'Request assistance',
  },
  resendPasscode: {
    circleColor: Colors.white,
    description: 'Please check your spam folder or search ‘Naked Passcode’ in your mailbox.',
    iconColor: Colors.green,
    title: 'Waiting for your passcode?',
    statusIcon: loadIcon('na-search-mail'),
    submitText: 'Resend now',
  },
  sessionExpired: {
    headerContent: (
      <IconContainer>
        <NakedIcon
          name="hourglass"
          color={Colors.green}
          height={HOURGLASS_ICON_SIZE}
          width={HOURGLASS_ICON_SIZE}
        />
      </IconContainer>
    ),
    content: (
      <Description>
        Every once in a while we need to make sure it&apos;s still you. Please log in again.
      </Description>
    ),
    hideCloseButton: true,
    onClose: () => true,
    submitText: 'Log in',
    title: "You've been logged out",
  },
  /** Extended cover options */
  extendedCoverOptions: {
    accentColor: Colors.white,
  },
  /** Extras modal */
  extras: {
    submitText: 'Done',
  },
  /** Get covered first */
  getCovered: {
    submitText: 'Okay',
    title: "Once you're covered by Naked...",
  },
  /** Hollard underwriting details */
  hollardUnderwriting: {
    content: (
      <Container px={`${PADDING_HORIZONTAL}px`} alignItems="center">
        <NakedIcon color={Colors.black} name="naked-wordmark" size={ICON_SIZE} />
        <Container py={`${PADDING_VERTICAL}px`}>
          <Light textAlign="center">
            Naked Financial Technology Pty Ltd is an authorised Financial Services Provider (FSP
            48822). Policies are underwritten by Hollard Specialist Insurance Limited, a Licensed
            Non-Life Insurer and authorised Financial Services Provider.
          </Light>
        </Container>
        <NakedIcon color={Colors.black} name="hollard" size={ICON_SIZE} />
      </Container>
    ),
    submitText: 'Close',
  },
  /** Make a change confirmation for your details */
  makeAChange: {
    content: (
      <Description>
        Please drop us a mail at <Medium color={Colors.green}>help@naked.insure</Medium> to let us
        know what changed
      </Description>
    ),
    submitText: 'Okay',
    title: "Something's changed?",
  },
  /** Remove basket item error */
  removeBasketItemError: {
    alertType: AlertType.Error,
    cancelText: 'Cancel',
    description:
      "Sorry, we couldn't delete your quote. Please check your connection and try again.",
    submitText: 'Try again',
    title: 'Please try again.',
  },
  /** Confirmation for removing contents */
  removeContentsConfirmation: {
    cancelText: 'Cancel',
    description:
      'By removing contents cover, you’re also removing cover for portable items over R5000.\n\nYou can get cover for those separately by adding them as individual items.',
    submitText: 'Go ahead',
    title: 'Heads up!',
  },
  /** Confirmation for removing valuables */
  removeValuableConfirmation: {
    cancelText: 'Cancel',
    submitText: 'Remove items',
    title: 'Remove items?',
  },
  /** Send message confirmation */
  sendMessage: {
    alertType: AlertType.Success,
    description: "We'll get back to you as soon as possible.",
    submitText: 'Okay',
    title: 'Message sent!',
  },
  /** Modal displaying a list of single item covers */
  singleItemsModal: {},
  /** Summary confirmation and error events  */
  summaryEvents: {},
  /** Update active card confirmation */
  updateActiveCard: {
    alertType: AlertType.Warning,
    cancelText: 'Cancel',
    description: 'Please note that your account payment details will be updated',
    submitText: 'Okay',
    title: 'Update your card details',
  },
  /** Value entry for a given valuable */
  valuableEntry: {},
  /** Confirmation for addition of a valuable to a cover */
  valuableEntryConfirmation: {
    hideCloseButton: true,
    submitText: 'Okay',
    title: 'Heads up!',
  },
  /** Confirmation for removal */
  valuableRemoval: {
    cancelText: 'Cancel',
    submitText: 'Remove items',
    title: 'Remove items?',
  },
  /** Payment still processing */
  paymentStillProcessing: {
    headerContent: (
      <IconContainer>
        <NakedIcon
          name="payment-process"
          color={Colors.green}
          height={PAYMENT_PROCESS_ICON_SIZE}
          width={PAYMENT_PROCESS_ICON_SIZE}
        />
      </IconContainer>
    ),
    content: (
      <Description>
        <Light fontSize={FontSize.Medium}>
          It&apos;s taking us longer than expected to process your payment. Kindly contact
          <Bold fontSize={FontSize.Medium} color={Colors.green}>
            {' '}
            help@naked.insure
          </Bold>{' '}
          and we&apos;ll get it sorted.
        </Light>
      </Description>
    ),
    submitText: 'Drop us a mail',
    title: 'Still processing...',
  },
  assetChangesReset: {
    alertType: AlertType.Warning,
    cancelText: 'Back to changes',
    description: "You'll lose the changes you've made.",
    submitText: "That's okay",
    title: 'Heads up!',
  },
  chatReset: {
    alertType: AlertType.Warning,
    cancelText: 'Continue chat',
    description: 'Our chat will restart if you go back.',
    submitText: 'Go Back',
    title: 'Heads up!',
  },
  summaryChatReset: {
    description: 'This will take you back to the first question.',
    submitText: 'Start Over?',
    title: (
      <Light color={Colors.black} fontSize={FontSize.Regular}>
        Start Over?
      </Light>
    ),
  },
  verifyUserConsent: {
    headerContent: (
      <IconContainer style={{ alignSelf: 'center', marginTop: -24 }}>
        <RoseModalImage src={ROSE_IMAGE} />
      </IconContainer>
    ),
    content: (
      <Container height={'50%'} width={'100%'} alignSelf="center">
        <Description>
          <Light fontSize={18} style={{ lineHeight: 28 }}>
            Do you want to save this quote?
          </Light>
        </Description>
      </Container>
    ),
    submitText: 'Save quote',
    cancelText: 'No thanks',
  },
  prospectExpiredError: {
    headerContent: (
      <IconContainer style={{ alignSelf: 'center', marginTop: -24 }}>
        <RoseModalImage src={ROSE_IMAGE} />
      </IconContainer>
    ),
    content: (
      <ProspectExpiredContent>
        <ProspectExpiredHeading>This quote is no longer available</ProspectExpiredHeading>
        <ProspectExpiredDescription>
          I&apos;m here if you want to get a fresh one.
        </ProspectExpiredDescription>
      </ProspectExpiredContent>
    ),
    submitText: 'Get a new quote',
  },
  paymentCompleted: {
    alertType: AlertType.Warning,
    description: 'Please note that your account payment details will be updated',
    submitText: 'Okay',
    title: 'Update your card details',
  },
  inlineChatError: {
    submitText: 'Retry',
    title: (
      <Light color={Colors.black} fontSize={FontSize.H2}>
        Sorry, we lost you there!
      </Light>
    ),
    description: 'We’re having a hard time connecting. Mind trying again?',
  },
  shortfall: {
    description:
      "Covers you if your outstanding car loan is more than the car's retail value, at the time of claim.",
    title: (
      <Bold color={Colors.black} fontSize={FontSize.H4}>
        Credit shortfall cover
      </Bold>
    ),
    accentColor: Colors.white,
  },
  entry: {
    animationIn: 'slideInUp' as const,
    animationOut: 'slideOutDown' as const,
    animationInTiming: 100,
    animationOutTiming: 100,
  },
  popup: {},
  coverSummarySent: {
    alertType: AlertType.Success,
    submitText: 'Okay',
    title: 'Success!',
  },
  coverSummaryError: {
    alertType: AlertType.Error,
    title: 'Oops!',
    submitText: 'Okay',
    content: (
      <>
        <Description>
          Please try again or contact us on
          <Bold color={Colors.green}> help@naked.insure</Bold>
        </Description>
      </>
    ),
  },
  basketSummarySent: {
    alertType: AlertType.Success,
    submitText: 'Okay',
    title: 'Success!',
  },
  basketSummaryError: {
    alertType: AlertType.Error,
    title: 'Oops!',
    submitText: 'Okay',
    content: (
      <>
        <Description>
          Please try again or contact us on
          <Bold color={Colors.green}> help@naked.insure</Bold>
        </Description>
      </>
    ),
  },
  basketCheckoutError: {
    alertType: AlertType.Error,
    title: 'Oops!',
    submitText: 'Okay',
    content: (
      <Description>
        You&apos;ve given different answers to the same question. Please clear your basket and try
        again.
      </Description>
    ),
  },
  covid19Rate: {
    title: 'COVID-19 Rates',
    submitText: 'Okay',
    accentColor: Colors.blue,
  },
  yourDetails: {
    title: 'Your details',
    styleProps: {
      title: {
        fontSize: FontSize.H4,
        fontWeight: FontWeight.Bold,
        backgroundColor: Colors.lightGrey,
        paddingTop: 20,
        marginTop: -50,
        height: 62,
        zIndex: -1,
      },
    },
  },
  editItemValue: {},
  addItemValue: {
    hideCloseButton: true,
  },
  youtubeVideo: {},
  buyOnApp: {},
  removeBasketItem: {},
};
